import { useEffect, useMemo, useState } from 'react'
import type { Recipient } from '@indipia/shared-features'
import {
  previewScheduleShare, previewErrorMessage,
  type SharePreviewGroup, type SharePreviewRecipient, type SharePreviewResponse,
} from '../api/scheduleShareApi'

export interface PreviewState {
  status: 'idle' | 'loading' | 'ready' | 'error'
  data?: SharePreviewResponse
  error?: string
}

/** 일반(반장·거래처) 미리보기 대상 — 수신자 목록의 첫 번째 공종 행 */
export interface GeneralPreviewTarget {
  recipient: SharePreviewRecipient
  workTypeName: string
}

/** 수신자 목록에서 일반 그룹 미리보기 대상을 고른다. 공종·파트너 번호가 확정되지 않은 수신자는 제외. */
export function pickGeneralTarget(recipients: Recipient[]): GeneralPreviewTarget | null {
  for (const r of recipients) {
    if (r.recipientType === 'CUSTOMER' || r.id == null) continue
    if (r.workTypesStatus !== 'ready') continue
    const w = r.workTypes?.[0]
    if (!w) continue
    return {
      recipient: { name: r.name, recipientType: r.recipientType, workTypeCode: w.code, partnerId: r.id },
      workTypeName: w.name,
    }
  }
  return null
}

function usePreview(siteId: number | undefined, group: SharePreviewGroup, recipient: SharePreviewRecipient | null): PreviewState {
  const [state, setState] = useState<PreviewState>({ status: 'idle' })
  const key = useMemo(
    () => (siteId != null && recipient ? JSON.stringify([siteId, group, recipient]) : null),
    [siteId, group, recipient],
  )

  useEffect(() => {
    if (key == null || siteId == null || recipient == null) { setState({ status: 'idle' }); return }
    const controller = new AbortController()
    setState({ status: 'loading' })
    previewScheduleShare({ shareType: 'SCHEDULE_SHARE', siteId, group, recipient }, controller.signal)
      .then(data => { if (!controller.signal.aborted) setState({ status: 'ready', data }) })
      .catch(err => { if (!controller.signal.aborted) setState({ status: 'error', error: previewErrorMessage(err) }) })
    return () => controller.abort()
    // key 가 siteId·group·recipient 를 모두 담고 있어 이것만 의존으로 둔다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return state
}

/**
 * 스케줄 공유 알림톡 서버 렌더 미리보기 — 고객·일반(반장·거래처) 두 그룹을 각각 조회한다.
 * 문구·버튼·만료는 서버가 준 값만 쓰고 화면에서 조립·계산하지 않는다.
 */
export function useScheduleSharePreview(
  siteId: number | undefined,
  customerName: string,
  recipients: Recipient[],
) {
  const customerRecipient = useMemo<SharePreviewRecipient | null>(() => {
    const c = recipients.find(r => r.recipientType === 'CUSTOMER')
    return { name: c?.name ?? customerName, recipientType: 'CUSTOMER' }
  }, [recipients, customerName])
  const generalTarget = useMemo(() => pickGeneralTarget(recipients), [recipients])
  const generalRecipient = useMemo(() => generalTarget?.recipient ?? null, [generalTarget])

  const customer = usePreview(siteId, 'CUSTOMER', customerRecipient)
  const general = usePreview(siteId, 'GENERAL', generalRecipient)
  return { customer, general, generalTarget }
}
